// The walkthrough lessons and the use cases, listed the same way on Home and
// in the Playground. See Design > Console, "Lessons".

import type { JSX } from "preact";
import { ChoiceRow, List } from "../../../ui/src/index.ts";
import type { Lesson } from "../domain/lesson.ts";
import { perMillion } from "../domain/money.ts";
import type { UseCase } from "./store.ts";
import { startFromText } from "./actions.ts";
import { href } from "./router.ts";

// One picture per lesson illustration; a new illustration fails to compile until it has one.
const ART: Record<Lesson["illustration"], JSX.Element> = {
  answer: <><path d="M14 18l30-8 34 14-6 40-40 6-20-22z" class="cs-art-tint" /><circle cx="46" cy="40" r="5" class="cs-art-accent" /></>,
  bid: <><rect x="14" y="44" width="16" height="22" rx="3" class="cs-art-plain" /><rect x="40" y="26" width="16" height="40" rx="3" class="cs-art-tint" /><rect x="66" y="12" width="16" height="54" rx="3" class="cs-art-accent" /></>,
  missing: <><rect x="12" y="20" width="72" height="40" rx="8" class="cs-art-card" /><rect x="22" y="34" width="22" height="12" rx="3" class="cs-art-tint cs-art-nostroke" /><rect x="50" y="34" width="24" height="12" rx="3" class="cs-art-dashed-accent" /></>,
  scope: <><circle cx="34" cy="40" r="20" class="cs-art-tint" /><circle cx="70" cy="40" r="10" class="cs-art-dashed" /></>,
  refund: <><path d="M30 26a20 20 0 1 1-4 22" class="cs-art-line" /><path d="M22 18v12h12" class="cs-art-line" /><circle cx="48" cy="40" r="6" class="cs-art-accent cs-art-nostroke" /></>,
};

/** A lesson's small line illustration, drawn with the --wh-* tokens in app.css. */
export const LessonArt = ({ kind }: { kind: Lesson["illustration"] }) => (
  <svg class="cs-lesson__art" width="48" height="40" viewBox="0 0 96 80" fill="none" aria-hidden="true">{ART[kind]}</svg>
);

/** The lessons as one raised list: each row a link with its illustration, title, blurb and response tag. */
export function LessonList({ lessons }: { lessons: ReadonlyArray<Lesson> }) {
  return (
    <List>
      {lessons.map((l) => (
        <a class="cs-lesson" href={href(`playground/${l.id}`)} key={l.id}>
          <LessonArt kind={l.illustration} />
          <span class="cs-lesson__text"><strong>{l.title}</strong><span>{l.blurb}</span></span>
          <span class="cs-lesson__tag">{l.badge}</span>
        </a>
      ))}
    </List>
  );
}

// A use case's label is the hub's description of what it answers, in lower case.
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** The use cases as one raised list, like the lessons: the question you'd send, what it covers under it, and its bid. Choosing one starts a request from it. */
export function UseCaseList({ useCases }: { useCases: ReadonlyArray<UseCase> }) {
  return (
    <List>
      {useCases.map((u) => (
        <ChoiceRow key={u.label} title={u.text} sub={sentence(u.label)} meta={`bid ${perMillion(u.maxCostPerAnswer)}`} onSelect={() => startFromText(u.text, u.maxCostPerAnswer)} />
      ))}
    </List>
  );
}
